import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera, Keyboard, RotateCcw, ScanLine, VideoOff } from "lucide-react";
import ActionButton from "../../components/ui/ActionButton";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeading } from "../../components/ui/PageHeading";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import PlantValue from "../../components/ui/PlantValue";
import QRCodePreview from "../../components/ui/QRCodePreview";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { usePlantOptions } from "../../features/plants/usePlantOptions";
import { useVerifyQrMutation } from "../../features/qr/qrApi";
import {
  assertSuccessfulResponse,
  getRequestErrorMessage,
  getResponseMessage,
} from "../../services/apiMessages";

const HIDDEN_RECORD_FIELDS = new Set(["qr_code_base64"]);
const INVALID_QR_MESSAGE =
  "Invalid QR. QR must contain exactly two values in value1_value2 format.";

const humanizeKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const renderPrimitiveValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const renderValue = (value) => {
  if (React.isValidElement(value)) return value;

  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) return "-";

    return (
      <div className="grid gap-1 rounded-md bg-white px-3 py-2">
        {entries.map(([key, itemValue]) => (
          <div key={key} className="grid gap-1 sm:grid-cols-[120px_1fr]">
            <span className="text-slate-500">{humanizeKey(key)}</span>
            <span className="break-all font-medium text-slate-900">
              {renderPrimitiveValue(itemValue)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return renderPrimitiveValue(value);
};

const getVerificationData = (response) => response?.data?.[0] || null;

const parseQrValue = (value) => {
  const normalizedValue = String(value || "").trim();
  const parts = normalizedValue.split("_");

  if (parts.length !== 2 || parts.some((part) => !part.trim())) {
    return { isValid: false, normalizedValue, parts: [] };
  }

  return {
    isValid: true,
    normalizedValue,
    parts: parts.map((part) => part.trim()),
  };
};

const QRVerification = () => {
  const [scanMode, setScanMode] = useState("text");
  const [selectedPlantId, setSelectedPlantId] = useState("");
  const [entityIdText, setEntityIdText] = useState("");
  const [cameraQrValue, setCameraQrValue] = useState("");
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [verifyQr, { isLoading }] = useVerifyQrMutation();
  const { options: plantOptions, isFetching: isLoadingPlants } = usePlantOptions();
  const toast = useToast();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameRequestRef = useRef(null);
  const streamRef = useRef(null);
  const lastScannedValueRef = useRef("");

  const textQrValue = useMemo(() => {
    const entityId = entityIdText.trim();

    return selectedPlantId && entityId ? `${selectedPlantId}_${entityId}` : "";
  }, [entityIdText, selectedPlantId]);

  const activeQrValue = scanMode === "text" ? textQrValue : cameraQrValue;
  const activeQrValidation = useMemo(
    () => parseQrValue(activeQrValue),
    [activeQrValue]
  );
  const hasCompleteTextInput = Boolean(selectedPlantId && entityIdText.trim());
  const textInputIsInvalid = hasCompleteTextInput && !activeQrValidation.isValid;
  const cameraDeviceOptions = useMemo(
    () =>
      videoDevices.map((device, index) => ({
        value: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      })),
    [videoDevices]
  );

  const record = result?.record || null;
  const recordEntries = useMemo(() => {
    if (!record) return [];

    return Object.entries(record).filter(
      ([key]) => !HIDDEN_RECORD_FIELDS.has(key)
    );
  }, [record]);

  const summaryItems = useMemo(
    () =>
      [
        ["Entity Type", result?.entity_type],
        ["Entity ID", result?.entity_id],
        [
          "Plant",
          result ? (
            <PlantValue
              plant={record?.plant_detail}
              fallbackPlantId={result?.plant_id || record?.plant_id}
            />
          ) : null,
        ],
        ["QR Value", result?.qr_value],
      ].filter(([, value]) => value !== null && value !== undefined && value !== ""),
    [record, result]
  );

  const stopCamera = useCallback(() => {
    if (frameRequestRef.current) {
      window.cancelAnimationFrame(frameRequestRef.current);
      frameRequestRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
  }, []);

  const verifyValue = useCallback(
    async (value) => {
      const validation = parseQrValue(value);

      if (!validation.normalizedValue || !validation.isValid) {
        setErrorMessage(INVALID_QR_MESSAGE);
        setResult(null);
        return;
      }

      try {
        const response = assertSuccessfulResponse(
          await verifyQr({ qr_value: validation.normalizedValue }).unwrap(),
          "Unable to verify QR code."
        );
        const nextResult = getVerificationData(response);

        setResult(nextResult);
        setErrorMessage("");
        setCameraError("");
        toast.success(getResponseMessage(response, "QR verified successfully."));
      } catch (error) {
        setResult(null);
        setErrorMessage(
          getRequestErrorMessage(error, "Unable to verify QR code.")
        );
      }
    },
    [toast, verifyQr]
  );

  const loadVideoDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === "videoinput");

    setVideoDevices(cameras);
    return cameras;
  }, []);

  const resetVerification = useCallback(() => {
    stopCamera();
    setSelectedPlantId("");
    setEntityIdText("");
    setCameraQrValue("");
    setCameraStatus("");
    setCameraError("");
    setErrorMessage("");
    setResult(null);
    lastScannedValueRef.current = "";
  }, [stopCamera]);

  const scanVideoFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !streamRef.current) {
      return;
    }

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (width && height) {
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d", { willReadFrequently: true });

        if (context) {
          context.drawImage(video, 0, 0, width, height);

          const imageData = context.getImageData(0, 0, width, height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          const scannedText = code?.data?.trim();

          if (scannedText && scannedText !== lastScannedValueRef.current) {
            const validation = parseQrValue(scannedText);

            lastScannedValueRef.current = scannedText;
            setCameraQrValue(scannedText);
            stopCamera();

            if (!validation.isValid) {
              setCameraStatus("Invalid QR captured.");
              setErrorMessage(INVALID_QR_MESSAGE);
              setResult(null);
              return;
            }

            setCameraStatus("QR captured.");
            verifyValue(scannedText);
            return;
          }
        }
      }
    }

    frameRequestRef.current = window.requestAnimationFrame(scanVideoFrame);
  }, [stopCamera, verifyValue]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera access is not supported in this browser.");
      return;
    }

    stopCamera();
    setCameraQrValue("");
    setCameraError("");
    setErrorMessage("");
    setResult(null);
    setCameraStatus("Opening camera...");
    lastScannedValueRef.current = "";

    try {
      const devices = videoDevices.length ? videoDevices : await loadVideoDevices();
      const preferredDevice =
        selectedDeviceId ||
        devices.find((device) => /back|rear|environment/i.test(device.label))
          ?.deviceId ||
        devices[0]?.deviceId;
      const constraints = {
        audio: false,
        video: preferredDevice
          ? { deviceId: { exact: preferredDevice } }
          : { facingMode: { ideal: "environment" } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      await loadVideoDevices();
      if (!selectedDeviceId && preferredDevice) {
        setSelectedDeviceId(preferredDevice);
      }

      setIsCameraActive(true);
      setCameraStatus("Scanning...");
      frameRequestRef.current = window.requestAnimationFrame(scanVideoFrame);
    } catch (error) {
      stopCamera();
      setCameraStatus("");
      setCameraError(
        error?.message || "Unable to access the camera. Check browser permission."
      );
    }
  }, [
    loadVideoDevices,
    scanVideoFrame,
    selectedDeviceId,
    stopCamera,
    videoDevices,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasCompleteTextInput) {
      setErrorMessage("Plant and entity ID are required.");
      setResult(null);
      return;
    }

    await verifyValue(textQrValue);
  };

  const handleModeChange = (mode) => {
    setScanMode(mode);
    setErrorMessage("");
    setCameraError("");

    if (mode === "text") {
      stopCamera();
      return;
    }

    window.setTimeout(() => {
      startCamera();
    }, 0);
  };

  useEffect(() => {
    if (scanMode === "camera") {
      loadVideoDevices().catch(() => {
        setVideoDevices([]);
      });
    }
  }, [loadVideoDevices, scanMode]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleDeviceChange = (value) => {
    setSelectedDeviceId(value);
    if (isCameraActive) {
      stopCamera();
    }
  };

  const showVisualQr = Boolean(
    activeQrValue && (scanMode === "camera" || activeQrValidation.isValid)
  );
  const statusMessage =
    errorMessage ||
    cameraError ||
    (scanMode === "text" && textInputIsInvalid ? INVALID_QR_MESSAGE : "") ||
    cameraStatus ||
    "";

  return (
    <div className="flex h-[calc(100dvh-92px)] flex-col overflow-hidden px-3 sm:px-5">
      <PageHeading title="QR Verification" className="mb-3 shrink-0" />

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-2 gap-3 lg:grid-cols-[minmax(320px,440px)_1fr] lg:grid-rows-1">
        <Card className="flex min-h-0 flex-col overflow-hidden p-3 sm:p-4">
          <div className="grid shrink-0 grid-cols-2 rounded-lg bg-slate-100 p-1">
            {[
              ["text", Keyboard, "Text"],
              ["camera", Camera, "Camera"],
            ].map(([mode, Icon, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => handleModeChange(mode)}
                className={`flex min-h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold transition ${
                  scanMode === mode
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-primary"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <form className="mt-3 shrink-0" onSubmit={handleSubmit}>
            <div className="grid h-[116px] grid-cols-1 gap-3 sm:h-[70px] sm:grid-cols-2">
              {scanMode === "text" ? (
                <>
                  <div className="min-w-0">
                    <Label htmlFor="plant_id">Plant</Label>
                    <PaginatedSelect
                      value={selectedPlantId}
                      onChange={(value) => {
                        setSelectedPlantId(value);
                        setErrorMessage("");
                        setResult(null);
                      }}
                      options={plantOptions}
                      disabled={isLoadingPlants && plantOptions.length === 0}
                      placeholder={isLoadingPlants ? "Loading plants..." : "Select plant"}
                    />
                  </div>

                  <div className="min-w-0">
                    <Label htmlFor="entity_id">Entity ID</Label>
                    <Input
                      id="entity_id"
                      value={entityIdText}
                      onChange={(event) => {
                        setEntityIdText(event.target.value);
                        setErrorMessage("");
                        setResult(null);
                      }}
                      placeholder="Enter ID"
                      autoComplete="off"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="min-w-0 sm:col-span-2">
                    <Label htmlFor="camera_device">Camera</Label>
                    <PaginatedSelect
                      value={selectedDeviceId}
                      onChange={handleDeviceChange}
                      options={cameraDeviceOptions}
                      disabled={isCameraActive}
                      placeholder="Default camera"
                    />
                  </div>
                </>
              )}
            </div>

            {scanMode === "text" && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <ActionButton
                  action="confirm"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!hasCompleteTextInput || textInputIsInvalid}
                  label="Verify QR"
                  className="w-full"
                />
                <ActionButton
                  action="cancel"
                  icon={RotateCcw}
                  variant="outline"
                  onClick={resetVerification}
                  label="Reset"
                  className="w-full"
                />
              </div>
            )}
          </form>

          <div className="relative mt-3 min-h-0 flex-1 overflow-hidden rounded-xl bg-slate-950">
            {scanMode === "camera" && (
              <>
                <video
                  ref={videoRef}
                  className={`h-full w-full object-cover ${
                    isCameraActive ? "opacity-100" : "opacity-0"
                  }`}
                  muted
                  playsInline
                />
                <canvas ref={canvasRef} className="hidden" />
                {isCameraActive && (
                  <div className="pointer-events-none absolute inset-6 rounded-xl border-2 border-white/80 shadow-[0_0_0_999px_rgba(15,23,42,0.35)]" />
                )}
              </>
            )}

            {!showVisualQr && !(scanMode === "camera" && isCameraActive) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900 text-slate-300">
                <ScanLine className="h-10 w-10" />
                <span className="text-sm font-medium">
                  {scanMode === "camera" ? "Camera ready" : "QR preview"}
                </span>
              </div>
            )}

            {showVisualQr && !(scanMode === "camera" && isCameraActive) && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50 p-3">
                <QRCodePreview
                  value={activeQrValue}
                  displayValue={activeQrValue}
                  label="QR verification"
                  size={128}
                  showValue
                  className="max-w-full"
                />
              </div>
            )}
          </div>

          <div className="mt-3 grid h-10 shrink-0 grid-cols-1 items-center">
            {statusMessage && (
              <p
                className={`truncate rounded-md px-3 py-2 text-sm font-medium ${
                  errorMessage || cameraError || textInputIsInvalid
                    ? "bg-red-50 text-red-600"
                    : "bg-sky-50 text-sky-700"
                }`}
                title={statusMessage}
              >
                {statusMessage}
              </p>
            )}
          </div>

          {scanMode === "camera" && (
            <div className="mt-3 grid shrink-0 grid-cols-2 gap-3">
              <ActionButton
                action="confirm"
                icon={Camera}
                onClick={startCamera}
                disabled={isCameraActive}
                label={cameraQrValue ? "Recapture" : "Start"}
                className="w-full"
              />
              <ActionButton
                action="cancel"
                icon={isCameraActive ? VideoOff : RotateCcw}
                variant="outline"
                onClick={isCameraActive ? stopCamera : resetVerification}
                label={isCameraActive ? "Stop" : "Reset"}
                className="w-full"
              />
            </div>
          )}
        </Card>

        <Card className="flex min-h-0 flex-col overflow-hidden p-3 sm:p-4">
          <div className="flex h-8 shrink-0 items-center justify-between gap-2">
            <h2 className="truncate text-base font-semibold text-slate-900">
              Verification Result
            </h2>
            <span
              className={`inline-flex w-fit shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                result
                  ? "bg-green-50 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {result ? "Verified" : "Pending"}
            </span>
          </div>

          <div className="mt-3 grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-4">
            {summaryItems.map(([label, value]) => (
              <div
                key={label}
                className="min-w-0 rounded-lg bg-slate-50 px-3 py-2"
              >
                <p className="truncate text-xs font-medium uppercase text-slate-500">
                  {label}
                </p>
                <div className="mt-1 min-w-0 text-sm font-semibold text-slate-900">
                  {renderValue(value)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-lg bg-slate-50">
            {recordEntries.length === 0 ? (
              <div className="flex h-full items-center justify-center px-4 text-center text-sm text-slate-500">
                No record selected
              </div>
            ) : (
              <div className="h-full overflow-y-auto">
                {recordEntries.map(([key, value]) => (
                  <div
                    key={key}
                    className="grid gap-2 border-b border-white px-3 py-2 text-sm last:border-b-0 sm:grid-cols-[minmax(140px,180px)_1fr]"
                  >
                    <span className="truncate font-medium text-slate-600">
                      {humanizeKey(key)}
                    </span>
                    <span className="min-w-0 break-all text-slate-900">
                      {renderValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QRVerification;
